import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardSection,
  Layout,
  useApi,
  Tiles,
  SmallHeader,
  Badge,
  GridColumn,
  GridRow,
  Grid,
  TilesItem,
  LayoutItem,
  ContextMenu,
  IconButton,
  ContextMenuItem,
  Form,
  Checkbox,
  DateUtil,
  ContextMenuDivider,
} from '@axiom/ui';
import type { TopTask } from '@axiom/validation';
import moment from 'moment';
import { useNavigate } from 'react-router';

import { TopTasksUtil } from '../../utils/top-tasks-util';
import { TopTasksApi } from '../../api/top-tasks-api';
import { WorkspaceTopTaskCategoryFilterSchema } from '../../schema/workspace-top-task-category-filter-schema';
import type { WorkSpaceTopTaskCategoryFilter } from '../../schema/workspace-top-task-category-filter-schema';

import { WorkspaceTopTaskTile } from './WorkspaceTopTaskTile';
import { WorkspaceTopTasksNoResults } from './WorkspaceTopTasksNoResults';
import { WorkspaceTopTasksIssueModal } from './WorkspaceTopTasksIssueModal';
import { WorkspaceDismissTopTaskModal } from './WorkspaceDismissTopTaskModal';

export const WorkspaceTopTasks = () => {
  const navigate = useNavigate();
  const [{ data: topTasks }] = useApi(TopTasksApi.readTopTasks());
  const [topTaskInitialState, setTopTaskInitialState] = useState({
    SubmissionNeeded: true,
    TalentNearingIdle: true,
    TalentCheckIn: true,
    TalentCooling: true,
    TalentBioIssues: true,
    TalentFeedback: true,
    TalentOnboarding: true,
    snoozedTasks: false,
  });
  const [showDismissTopTaskModal, setShowDismissTopTaskModal] = useState(false);

  const [selectedTopTaskIssue, setSelectedTopTaskIssue] =
    useState<TopTask | null>(null);

  const sortedTopTasks = useMemo(() => {
    return [...topTasks].sort((a: TopTask, b: TopTask) => {
      return (
        moment().diff(b.taskExpectedCompletionDateTime) -
        moment().diff(a.taskExpectedCompletionDateTime)
      );
    });
  }, [topTasks]);
  const filteredTopTasks = useMemo(() => {
    const { snoozedTasks, ...filters } = topTaskInitialState;
    const { filteredTasks } = TopTasksUtil.getFilteredTopTasks({
      topTasks: sortedTopTasks,
      filters,
      snoozedTasks,
    });
    return filteredTasks;
  }, [sortedTopTasks, topTaskInitialState]);

  // Needed to keep Top Tasks in sync across navigation
  useEffect(() => {
    void TopTasksApi.refreshTopTasks();
  }, []);

  const handleTopTaskClick = (task: TopTask) => {
    if (TopTasksUtil.isCandidateIssue(task)) {
      setSelectedTopTaskIssue(task);
    } else if (TopTasksUtil.isReputationIssue(task)) {
      void navigate(`/workspace/talent-feedback`);
    } else {
      void navigate(TopTasksUtil.getTopTaskLink(task));
    }
  };
  return (
    <Grid name="WORKSPACE_TOP_TASKS" stretched>
      <GridRow gutterTop="16px">
        <GridColumn stretched>
          <Card stretched scrollable>
            <Form
              schema={WorkspaceTopTaskCategoryFilterSchema}
              name="TOP_TASK_CATEGORY_FILTER_FORM"
              initialValues={topTaskInitialState}
              submitOnChange
              onSubmit={(_, __, formData) => {
                setTopTaskInitialState(formData);
              }}
            >
              {({ values }) => {
                const { snoozedTasks, ...filters } = values;
                return (
                  <CardHeader>
                    <Layout
                      name="TOP_TASK_COUNT"
                      direction="horizontal"
                      horizontalGutter="8px"
                      position="space-between middle"
                    >
                      <LayoutItem>
                        <Layout
                          name="TOP_TASK_COUNT_DISPLAY"
                          position="middle"
                          horizontalGutter="8px"
                        >
                          <SmallHeader>
                            Top{' '}
                            {filteredTopTasks.length === 1 ? 'Task' : 'Tasks'}
                          </SmallHeader>
                          <Badge
                            name="TOP_TASK_COUNT_BADGE"
                            background="light blue"
                          >
                            {filteredTopTasks.length}
                          </Badge>
                        </Layout>
                      </LayoutItem>
                      <LayoutItem>
                        <Layout position="middle">
                          <Badge
                            name="TOP_TASK_FILTER_COUNT_BADGE"
                            background="light blue"
                          >
                            {
                              TopTasksUtil.getFilteredTopTasks({
                                topTasks: sortedTopTasks,
                                filters,
                                snoozedTasks:
                                  snoozedTasks as WorkSpaceTopTaskCategoryFilter['snoozedTasks'],
                              }).topTaskFilterCount
                            }
                          </Badge>
                          <ContextMenu
                            direction="left"
                            anchor={
                              <IconButton
                                name="FILTER_MENU_BUTTON"
                                variation="minimal"
                                icon="filter"
                              />
                            }
                          >
                            <ContextMenuItem>
                              <Checkbox
                                displayValue="Submission needed"
                                name="SubmissionNeeded"
                              />
                            </ContextMenuItem>
                            <ContextMenuItem>
                              <Checkbox
                                displayValue="Talent nearing idle"
                                name="TalentNearingIdle"
                              />
                            </ContextMenuItem>
                            <ContextMenuItem>
                              <Checkbox
                                displayValue="Talent check-in"
                                name="TalentCheckIn"
                              />
                            </ContextMenuItem>
                            <ContextMenuItem>
                              <Checkbox
                                displayValue="Talent feedback"
                                name="TalentFeedback"
                              />
                            </ContextMenuItem>
                            <ContextMenuItem>
                              <Checkbox
                                displayValue="Talent bio issues"
                                name="TalentBioIssues"
                              />
                            </ContextMenuItem>
                            <ContextMenuItem>
                              <Checkbox
                                displayValue="Talent cooling"
                                name="TalentCooling"
                              />
                            </ContextMenuItem>
                            <ContextMenuItem>
                              <Checkbox
                                displayValue="Talent onboarding"
                                name="TalentOnboarding"
                              />
                            </ContextMenuItem>
                            <ContextMenuDivider />
                            <ContextMenuItem>
                              <Checkbox
                                displayValue="All snoozed tasks"
                                name="snoozedTasks"
                              />
                            </ContextMenuItem>
                          </ContextMenu>
                        </Layout>
                      </LayoutItem>
                    </Layout>
                  </CardHeader>
                );
              }}
            </Form>
            <CardSection>
              {filteredTopTasks.length === 0 ? (
                <WorkspaceTopTasksNoResults name="TOP_TASKS_NO_RESULTS" />
              ) : (
                <Tiles columnWidth={1}>
                  {filteredTopTasks.map((task: TopTask) => (
                    <Layout
                      key={task.id}
                      position="middle"
                      horizontalGutter="10px"
                    >
                      <TilesItem
                        name={`TOP_TASK_TILE_${task.id}`}
                        onClick={() => {
                          handleTopTaskClick(task);
                        }}
                      >
                        <WorkspaceTopTaskTile
                          name={`TOP_TASK_TILE_CONTENT_${task.id}`}
                          task={task}
                        />
                      </TilesItem>
                      <ContextMenu
                        name={`TOP_TASK_ACTION_MENU_${task.id}`}
                        direction="left"
                      >
                        {task.reputationDetailsId ? (
                          <ContextMenuItem
                            name={`TOP_TASK_DISMISS_BUTTON_${task.id}`}
                            onClick={() => {
                              setSelectedTopTaskIssue(task);
                              setShowDismissTopTaskModal(true);
                            }}
                          >
                            Dismiss
                          </ContextMenuItem>
                        ) : null}
                        <ContextMenuItem
                          name={`TOP_TASK_SNOOZE_BUTTON_${task.id}`}
                          disabled={
                            !DateUtil.isTodayOneOrMoreBusinessDaysBefore(
                              task.taskExpectedCompletionDateTime
                            ) ||
                            (task.snoozedAt != null &&
                              DateUtil.isSnoozedToday(task.snoozedAt))
                          }
                          onClick={async () => {
                            await TopTasksApi.createTopTaskSnooze(task.id);
                            await TopTasksApi.refreshTopTasks();
                          }}
                        >
                          Snooze for 1 day
                        </ContextMenuItem>
                      </ContextMenu>
                    </Layout>
                  ))}
                </Tiles>
              )}
            </CardSection>
          </Card>
        </GridColumn>
      </GridRow>
      {selectedTopTaskIssue ? (
        <WorkspaceTopTasksIssueModal
          name="TOP_TASKS_ISSUE_MODAL"
          topTask={selectedTopTaskIssue}
          onClose={() => {
            setSelectedTopTaskIssue(null);
          }}
        />
      ) : null}
      {showDismissTopTaskModal && selectedTopTaskIssue ? (
        <WorkspaceDismissTopTaskModal
          topTask={selectedTopTaskIssue}
          onClose={() => {
            setSelectedTopTaskIssue(null);
            setShowDismissTopTaskModal(false);
          }}
        />
      ) : null}
    </Grid>
  );
};
